export default function mergePOSData(rawData, toastDataCur, toastDataComp, storeMap) {

  // Handle case where rawData is empty/null/undefined
  const safeRawData = Array.isArray(rawData) ? rawData : [];

  const shortNameLookup = new Map()
  storeMap.forEach((value) => {
    shortNameLookup.set(value.shortName, value)
  })

  // Initialize result array
  const result = [];

  // Process company and franchise data separately
  ['company', 'franchise'].forEach(type => {
    const rawItem = safeRawData.find(item => item.pageId?.includes(`r0002_LSD_${type}`));
    const curItem = toastDataCur.find(item => item.pageId?.includes(`r0002_LSD_${type}`))
    const compItem = toastDataComp.find(item => item.pageId?.includes(`r0002_LSD_${type}`))

    // Check if both sources have data
    const hasRawData = rawItem && !rawItem.pageId.includes('_empty') && rawItem.data && rawItem.data.length > 0;
    const hasToastDataCur = curItem && !curItem.pageId.includes('_empty') && curItem.data && curItem.data.length > 0;
    const hasToastDataComp = compItem && !compItem.pageId.includes('_empty') && compItem.data && compItem.data.length > 0;

    // If neither source has data, add empty result
    if (!hasRawData && !hasToastDataCur && !hasToastDataComp) {
      result.push({
        pageId: `r0002_LSD_${type}_empty`,
        data: []
      });
      return;
    }

    // Initialize merged data with raw data structure
    const mergedData = {
      pageId: hasRawData ? rawItem.pageId : `r0002_LSD_${type}`,
      data: []
    };

    const processStoreRow = (item) => {
      if (item.rowType === 'storeRow'  || item.rowType === 'otherStoreRow') {
        const storeInfo = shortNameLookup.get(item.rowData.store)
        const isEstablished = storeInfo?.status === 'Established'
        const isClosed = storeInfo?.status === 'Closed'

        // Log closed store detection
        if (isClosed) {
          console.log(`mergePOSData2 - Closed store detected: ${item.rowData.store}, status: ${storeInfo?.status}`);
        }

        // Extract thresholds and remove GraphQL __typename field
        const thresholds = storeInfo?.thresholds ? { ...storeInfo.thresholds } : {}
        delete thresholds.__typename

        return {
          rowType: isEstablished ? 'storeRow' : 'otherStoreRow',
          storeId: storeInfo?.storeRecordId, // Add storeId for threshold lookups
          thresholds: thresholds, // Add threshold assignments from storeMap
          rowData: {
            ...item.rowData,
            manager: isEstablished ? storeInfo.area : 'Others'
          }
        }
      }
      return item
    }

    // Helper function to calculate standardized SPLH values
    const calculateStandardizedSPLH = (rowData, yearPrefix = 'c') => {
      const netSales = convertCurrencyStringToNumber(rowData[`${yearPrefix}NSTotal`] || '0');
      const fohHours = parseFloat(rowData[`${yearPrefix}FOHHours`] || 0);
      const bohHours = parseFloat(rowData[`${yearPrefix}BOHHours`] || 0);
      const totalHours = fohHours + bohHours;
      
      const totalSPLH = totalHours === 0 ? 0 : netSales / totalHours;
      const fohSPLH = fohHours === 0 ? 0 : netSales / fohHours;
      const bohSPLH = bohHours === 0 ? 0 : netSales / bohHours;
      
      // console.log(`SPLH Calculation for ${rowData.store} (${yearPrefix === 'c' ? 'Current' : 'Previous'} Year):`, {
      //   rawData: {
      //     [`${yearPrefix}NSTotal`]: rowData[`${yearPrefix}NSTotal`],
      //     [`${yearPrefix}FOHHours`]: rowData[`${yearPrefix}FOHHours`],
      //     [`${yearPrefix}BOHHours`]: rowData[`${yearPrefix}BOHHours`]
      //   },
      //   parsed: {
      //     netSales,
      //     fohHours,
      //     bohHours,
      //     totalHours
      //   },
      //   calculated: {
      //     totalSPLH,
      //     fohSPLH,
      //     bohSPLH
      //   }
      // });
      
      return {
        [`${yearPrefix}SPLHTotal`]: Math.round(totalSPLH),
        [`${yearPrefix}SPLHFOH`]: Math.round(fohSPLH),
        [`${yearPrefix}SPLHBOH`]: Math.round(bohSPLH)
      };
    };

    if (hasRawData) {

      // First process the store rows to set correct types and managers
      mergedData.data = rawItem.data.map(processStoreRow)

      if (hasToastDataComp) {
        compItem.data.forEach(compRow => {
          const idx = mergedData.data.findIndex(r =>
            (r.rowType==='storeRow'||r.rowType==='otherStoreRow')
            && r.rowData.store === compRow.rowData.store
          )
          if (idx !== -1) {
            const target = mergedData.data[idx].rowData

            // for each cXXX in compRow, write to pXXX on the target. Sums apply to
            // additive fields; rate fields (pLaborPct, pFOH/pBOH) end up as
            // nonsense string sums here and are re-derived from totals below.
            Object.keys(compRow.rowData)
              .filter(k => k.startsWith('c'))
              .forEach(k => {
                const pKey = 'p' + k.slice(1)
                const alohaVal = convertCurrencyStringToNumber(target[pKey] || '$0')
                const toastVal = convertCurrencyStringToNumber(compRow.rowData[k])
                const sumVal = alohaVal + toastVal
                target[pKey] =  `${sumVal.toLocaleString(undefined,{minimumFractionDigits:0, maximumFractionDigits:0})}`
              })

            // Sum prior-year labor hours instead of overwriting with Toast-only.
            // Aloha keeps pFOHHours/pBOHHours after transformAlohaKeys; Toast
            // provides the regular+overtime split.
            const compToastFOHHours = (compRow.rowData._fohOvertimeHours || 0)
              + (compRow.rowData._fohRegularHours || 0);
            const compToastBOHHours = (compRow.rowData._bohOvertimeHours || 0)
              + (compRow.rowData._bohRegularHours || 0);
            target.pFOHHours = convertCurrencyStringToNumber(target.pFOHHours) + compToastFOHHours;
            target.pBOHHours = convertCurrencyStringToNumber(target.pBOHHours) + compToastBOHHours;

            // Re-derive prior-year rate display fields from the summed totals so
            // the nonsense string sums produced by the cXXX→pXXX loop above don't
            // reach the report. Stores without cur Toast data won't pass through
            // any later parsedData re-derivation, so these must be correct here.
            const pNSTotalNum = convertCurrencyStringToNumber(target.pNSTotal);
            const pLaborDollarsNum = convertCurrencyStringToNumber(target.pLaborDollars);
            target.pLaborPct = pNSTotalNum > 0
              ? `${((pLaborDollarsNum / pNSTotalNum) * 100).toFixed(2)}%`
              : '0.00%';
            target.pFOH = target.pFOHHours > 0
              ? `${Math.round(pNSTotalNum / target.pFOHHours)}`
              : '0';
            target.pBOH = target.pBOHHours > 0
              ? `${Math.round(pNSTotalNum / target.pBOHHours)}`
              : '0';
          } else {
            // Store exists in Toast comp data but not in Aloha - add it
            console.log(`mergePOSData2 - Adding Toast-only comp store: ${compRow.rowData.store}`);
            const processedItem = processStoreRow(compRow)
            const newRow = {
              rowType: processedItem.rowType,
              storeId: processedItem.storeId,
              thresholds: processedItem.thresholds || {},
              rowData: {
                ...processedItem.rowData,
                // Initialize all current fields to zero
                cCCTotal: '0',
                cCCDineIn: '0',
                cCCToGo: '0',
                cCCDelivery: '0',
                cNSTotal: '$0',
                cNSDineIn: '$0',
                cNSToGo: '$0',
                cNSDelivery: '$0',
                cGSDelivery: '$0',
                cACTotal: '0.00',
                cACDineIn: '0.00',
                cACToGo: '0.00',
                cACDelivery: '0.00',
                cSPLHTotal: '0',
                cBOH: '$0',
                cFOH: '$0',
                cOTTotal: '$0',
                cOTBOH: '$0',
                cOTFOH: '$0',
                cLaborPct: '0.00%',
                cLaborDollars: '$0',
                cFOHHours: 0,
                cBOHHours: 0
              }
            }
            
            // Copy comparison data from Toast
            Object.keys(compRow.rowData)
              .filter(k => k.startsWith('c'))
              .forEach(k => {
                const pKey = 'p' + k.slice(1)
                newRow.rowData[pKey] = compRow.rowData[k]
              })
              
            // Handle previous year hours data
            if (compRow.rowData._fohOvertimeHours !== undefined && compRow.rowData._fohRegularHours !== undefined) {
              newRow.rowData.pFOHHours = compRow.rowData._fohOvertimeHours + compRow.rowData._fohRegularHours;
            }
            if (compRow.rowData._bohOvertimeHours !== undefined && compRow.rowData._bohRegularHours !== undefined) {
              newRow.rowData.pBOHHours = compRow.rowData._bohOvertimeHours + compRow.rowData._bohRegularHours;
            }
            
            mergedData.data.push(newRow)
          }
        })
      }

      // If we have Toast data, merge it with the copied raw data
      if (hasToastDataCur) {

        curItem.data.forEach(toastStoreItem => {
          if (toastStoreItem.rowType === 'storeRow' || toastStoreItem.rowType === 'otherStoreRow') {
            const existingStoreIndex = mergedData.data.findIndex(
              item => (item.rowType === 'storeRow' || item.rowType === 'otherStoreRow') && 
                      item.rowData.store === toastStoreItem.rowData.store
            );

            if (existingStoreIndex !== -1) {
              // Sum Toast current-year values into the Aloha values already on the
              // merged row. Mirrors the comp-side sum above. Prior behavior was to
              // overwrite, which silently dropped Aloha sales for stores whose
              // toastGoLiveDate fell inside the queried current range.
              const existingStore = mergedData.data[existingStoreIndex];
              const mergedRowData = { ...existingStore.rowData };

              // Additive cur-year fields. Rate fields (cAC*, cSPLH*, SPLH-by-shift
              // cFOH/cBOH, cLaborPct) are derived from these totals further below.
              const additiveCurFields = [
                'cCCTotal', 'cCCDineIn', 'cCCToGo', 'cCCDelivery',
                'cNSTotal', 'cNSDineIn', 'cNSToGo', 'cNSDelivery', 'cGSDelivery',
                'cOTTotal', 'cOTBOH', 'cOTFOH',
                'cLaborDollars',
              ];
              additiveCurFields.forEach(k => {
                const alohaVal = convertCurrencyStringToNumber(mergedRowData[k] || '$0');
                const toastVal = convertCurrencyStringToNumber(toastStoreItem.rowData[k] || '$0');
                mergedRowData[k] = alohaVal + toastVal;
              });

              // Cur-year labor hours. Aloha keeps cFOHHours/cBOHHours after
              // transformAlohaKeys; Toast provides the regular+overtime split.
              const toastFOHHours = (toastStoreItem.rowData._fohOvertimeHours || 0)
                + (toastStoreItem.rowData._fohRegularHours || 0);
              const toastBOHHours = (toastStoreItem.rowData._bohOvertimeHours || 0)
                + (toastStoreItem.rowData._bohRegularHours || 0);
              mergedRowData.cFOHHours = convertCurrencyStringToNumber(existingStore.rowData.cFOHHours) + toastFOHHours;
              mergedRowData.cBOHHours = convertCurrencyStringToNumber(existingStore.rowData.cBOHHours) + toastBOHHours;

              // Parse values needed for calculations
              const parsedData = {
                ...mergedRowData,
                cCCTotal: parseNumberString(mergedRowData.cCCTotal),
                pCCTotal: parseNumberString(mergedRowData.pCCTotal),
                cCCDineIn: parseNumberString(mergedRowData.cCCDineIn),
                pCCDineIn: parseNumberString(mergedRowData.pCCDineIn),
                cCCToGo: parseNumberString(mergedRowData.cCCToGo),
                pCCToGo: parseNumberString(mergedRowData.pCCToGo),
                cCCDelivery: parseNumberString(mergedRowData.cCCDelivery),
                pCCDelivery: parseNumberString(mergedRowData.pCCDelivery),
                cNSTotal: convertCurrencyStringToNumber(mergedRowData.cNSTotal),
                pNSTotal: convertCurrencyStringToNumber(mergedRowData.pNSTotal),
                cNSDineIn: convertCurrencyStringToNumber(mergedRowData.cNSDineIn),
                pNSDineIn: convertCurrencyStringToNumber(mergedRowData.pNSDineIn),
                cNSToGo: convertCurrencyStringToNumber(mergedRowData.cNSToGo),
                pNSToGo: convertCurrencyStringToNumber(mergedRowData.pNSToGo),
                cNSDelivery: convertCurrencyStringToNumber(mergedRowData.cNSDelivery),
                pNSDelivery: convertCurrencyStringToNumber(mergedRowData.pNSDelivery),
                cGSDelivery: convertCurrencyStringToNumber(mergedRowData.cGSDelivery),
                pGSDelivery: convertCurrencyStringToNumber(mergedRowData.pGSDelivery),
                cBOH: convertCurrencyStringToNumber(mergedRowData.cBOH),
                cFOH: convertCurrencyStringToNumber(mergedRowData.cFOH),
                cOTTotal: convertCurrencyStringToNumber(mergedRowData.cOTTotal),
                cOTBOH: convertCurrencyStringToNumber(mergedRowData.cOTBOH),
                cOTFOH: convertCurrencyStringToNumber(mergedRowData.cOTFOH),
                cLaborPct: parsePercentString(mergedRowData.cLaborPct)
              };

              // Calculate averages for both years
              parsedData.cACTotal = calculateAverageCheck(parsedData.cNSTotal, parsedData.cCCTotal);
              parsedData.pACTotal = calculateAverageCheck(parsedData.pNSTotal, parsedData.pCCTotal);
              
              parsedData.cACDineIn = calculateAverageCheck(parsedData.cNSDineIn, parsedData.cCCDineIn);
              parsedData.pACDineIn = calculateAverageCheck(parsedData.pNSDineIn, parsedData.pCCDineIn);
              
              parsedData.cACToGo = calculateAverageCheck(parsedData.cNSToGo, parsedData.cCCToGo);
              parsedData.pACToGo = calculateAverageCheck(parsedData.pNSToGo, parsedData.pCCToGo);
              
              parsedData.cACDelivery = calculateAverageCheck(parsedData.cNSDelivery, parsedData.cCCDelivery);
              parsedData.pACDelivery = calculateAverageCheck(parsedData.pNSDelivery, parsedData.pCCDelivery);

              // STANDARDIZED SPLH CALCULATION - Recalculate for both years
              const currentSPLH = calculateStandardizedSPLH(parsedData, 'c');
              Object.assign(parsedData, currentSPLH);
              
              // Only calculate previous year SPLH if we have previous year data
              if (parsedData.pNSTotal && parsedData.pFOHHours !== undefined && parsedData.pBOHHours !== undefined) {
                const previousSPLH = calculateStandardizedSPLH(parsedData, 'p');
                Object.assign(parsedData, previousSPLH);
              }

              // Recalculate the remaining cur-year rate fields from the summed
              // totals. cAC* and cSPLH* are handled above; cLaborPct and the
              // SPLH-by-shift display columns cFOH/cBOH need explicit re-derivation
              // so a sum of two rate strings doesn't end up in the output.
              parsedData.cLaborPct = parsedData.cNSTotal > 0
                ? convertCurrencyStringToNumber(parsedData.cLaborDollars) / parsedData.cNSTotal
                : 0;
              parsedData.cFOH = parsedData.cFOHHours > 0
                ? Math.round(parsedData.cNSTotal / parsedData.cFOHHours)
                : 0;
              parsedData.cBOH = parsedData.cBOHHours > 0
                ? Math.round(parsedData.cNSTotal / parsedData.cBOHHours)
                : 0;

              parsedData.pct = calculatePercentageChange(parsedData.cNSTotal, parsedData.pNSTotal)

              // Format and update the store data
              mergedData.data[existingStoreIndex].rowData = formatStoreData(parsedData);
            } else {
              // Add new store that only exists in Toast data
              const processedItem = processStoreRow(toastStoreItem)
              
              const compRow = compItem.data.find(r => r.rowData.store === processedItem.rowData.store)
              const compValue = compRow
                ? compRow.rowData.cNSTotal
                : '0'

              const parsedData = {
                ...processedItem.rowData,
                cCCTotal: parseNumberString(processedItem.rowData.cCCTotal),
                cCCDineIn: parseNumberString(processedItem.rowData.cCCDineIn),
                cCCToGo: parseNumberString(processedItem.rowData.cCCToGo),
                cCCDelivery: parseNumberString(processedItem.rowData.cCCDelivery),
                cNSTotal: convertCurrencyStringToNumber(processedItem.rowData.cNSTotal),
                cNSDineIn: convertCurrencyStringToNumber(processedItem.rowData.cNSDineIn),
                cNSToGo: convertCurrencyStringToNumber(processedItem.rowData.cNSToGo),
                cNSDelivery: convertCurrencyStringToNumber(processedItem.rowData.cNSDelivery),
                cGSDelivery: convertCurrencyStringToNumber(processedItem.rowData.cGSDelivery),
                cBOH: convertCurrencyStringToNumber(processedItem.rowData.cBOH),
                cFOH: convertCurrencyStringToNumber(processedItem.rowData.cFOH),
                cOTTotal: convertCurrencyStringToNumber(processedItem.rowData.cOTTotal),
                cOTBOH: convertCurrencyStringToNumber(processedItem.rowData.cOTBOH),
                cOTFOH: convertCurrencyStringToNumber(processedItem.rowData.cOTFOH),
                cLaborPct: parsePercentString(processedItem.rowData.cLaborPct),
                pNSTotal: convertCurrencyStringToNumber(compValue),
                cFOHHours: processedItem.rowData._fohOvertimeHours + processedItem.rowData._fohRegularHours,
                cBOHHours: processedItem.rowData._bohOvertimeHours + processedItem.rowData._bohRegularHours
              };

              if (compRow) {
                Object.keys(compRow.rowData)
                  .filter(k => k.startsWith('c'))
                  .forEach(k => {
                    const pKey = 'p' + k.slice(1);
                    parsedData[pKey] = compRow.rowData[k]
                  })
                  const compData = compRow.rowData
                  parsedData.pNSTotal = convertCurrencyStringToNumber(compData.cNSTotal)
                  parsedData.pCCTotal = parseNumberString(compData.cCCTotal)
                  parsedData.pNSDineIn = convertCurrencyStringToNumber(compData.cNSDineIn);
                  parsedData.pCCDineIn = parseNumberString(compData.cCCDineIn);
                  parsedData.pNSToGo = convertCurrencyStringToNumber(compData.cNSToGo);
                  parsedData.pCCToGo = parseNumberString(compData.cCCToGo);
                  parsedData.pNSDelivery = convertCurrencyStringToNumber(compData.cNSDelivery);
                  parsedData.pCCDelivery = parseNumberString(compData.cCCDelivery);
                  parsedData.pGSDelivery = convertCurrencyStringToNumber(compData.cGSDelivery);
                  parsedData.pct = parsePercentString(compData.pct)
                  
                  // Extract previous year hours from Toast comp data
                  parsedData.pFOHHours = compData._fohOvertimeHours + compData._fohRegularHours
                  parsedData.pBOHHours = compData._bohOvertimeHours + compData._bohRegularHours
              }

              // Calculate averages
              parsedData.cACTotal = calculateAverageCheck(parsedData.cNSTotal, parsedData.cCCTotal);
              parsedData.cACDineIn = calculateAverageCheck(parsedData.cNSDineIn, parsedData.cCCDineIn);
              parsedData.cACToGo = calculateAverageCheck(parsedData.cNSToGo, parsedData.cCCToGo);
              parsedData.cACDelivery = calculateAverageCheck(parsedData.cNSDelivery, parsedData.cCCDelivery);
              parsedData.pACTotal = calculateAverageCheck(parsedData.pNSTotal, parsedData.pCCTotal);
              parsedData.pACDineIn = calculateAverageCheck(parsedData.pNSDineIn, parsedData.pCCDineIn);
              parsedData.pACToGo = calculateAverageCheck(parsedData.pNSToGo, parsedData.pCCToGo);
              parsedData.pACDelivery = calculateAverageCheck(parsedData.pNSDelivery, parsedData.pCCDelivery);

              // STANDARDIZED SPLH CALCULATION - Recalculate for both years
              const currentSPLH = calculateStandardizedSPLH(parsedData, 'c');
              Object.assign(parsedData, currentSPLH)
              
              // Only calculate previous year SPLH if we have previous year data
              if (parsedData.pNSTotal && parsedData.pFOHHours !== undefined && parsedData.pBOHHours !== undefined) {
                // parsedData.pSPLHTotal = calculateStandardizedSPLH(parsedData, 'p');
                const previousSPLH = calculateStandardizedSPLH(parsedData, 'p');
                Object.assign(parsedData, previousSPLH)
              }

              parsedData.pct = calculatePercentageChange(parsedData.cNSTotal, parsedData.pNSTotal)            

              mergedData.data.push({
                rowType: processedItem.rowType,
                storeId: processedItem.storeId, // Preserve storeId from processStoreRow
                thresholds: processedItem.thresholds || {}, // Preserve thresholds from processStoreRow
                rowData: formatStoreData(parsedData)
              })
            }
          }
        });
      }

      // RECALCULATE SPLH FOR ALL ALOHA-ONLY STORES
      mergedData.data.forEach((item, index) => {
        if ((item.rowType === 'storeRow' || item.rowType === 'otherStoreRow')) {
          // Check if this store has already been processed by Toast merge
          const hasToastData = item.rowData.cFOHHours !== undefined && item.rowData.cBOHHours !== undefined;
          if (hasToastData) {
            // Skip - this store was already fully processed in the Toast merge section
            return;
          }
          
          // Parse current data for Aloha-only stores
          const parsedData = {
            ...item.rowData,
            cCCTotal: parseNumberString(item.rowData.cCCTotal),
            cCCDineIn: parseNumberString(item.rowData.cCCDineIn),
            cCCToGo: parseNumberString(item.rowData.cCCToGo),
            cCCDelivery: parseNumberString(item.rowData.cCCDelivery),
            cNSTotal: convertCurrencyStringToNumber(item.rowData.cNSTotal),
            cNSDineIn: convertCurrencyStringToNumber(item.rowData.cNSDineIn),
            cNSToGo: convertCurrencyStringToNumber(item.rowData.cNSToGo),
            cNSDelivery: convertCurrencyStringToNumber(item.rowData.cNSDelivery),
            cGSDelivery: convertCurrencyStringToNumber(item.rowData.cGSDelivery),
            cBOH: convertCurrencyStringToNumber(item.rowData.cBOH),
            cFOH: convertCurrencyStringToNumber(item.rowData.cFOH),
            cOTTotal: convertCurrencyStringToNumber(item.rowData.cOTTotal),
            cOTBOH: convertCurrencyStringToNumber(item.rowData.cOTBOH),
            cOTFOH: convertCurrencyStringToNumber(item.rowData.cOTFOH),
            cLaborPct: parsePercentString(item.rowData.cLaborPct),
            cFOHHours: parseFloat(item.rowData.cFOHHours || 0),
            cBOHHours: parseFloat(item.rowData.cBOHHours || 0),
            // Don't re-parse p values - they're already formatted
            pFOHHours: parseFloat(item.rowData.pFOHHours || 0),
            pBOHHours: parseFloat(item.rowData.pBOHHours || 0)
          };

          // Calculate averages for current year
          parsedData.cACTotal = calculateAverageCheck(parsedData.cNSTotal, parsedData.cCCTotal);
          parsedData.cACDineIn = calculateAverageCheck(parsedData.cNSDineIn, parsedData.cCCDineIn);
          parsedData.cACToGo = calculateAverageCheck(parsedData.cNSToGo, parsedData.cCCToGo);
          parsedData.cACDelivery = calculateAverageCheck(parsedData.cNSDelivery, parsedData.cCCDelivery);

          // Recalculate SPLH for both current and previous year
          const currentSPLH = calculateStandardizedSPLH(parsedData, 'c');
          Object.assign(parsedData, currentSPLH);
          
          // Only calculate previous year SPLH if we have previous year data
          if (parsedData.pNSTotal && parsedData.pFOHHours !== undefined && parsedData.pBOHHours !== undefined) {
            const previousSPLH = calculateStandardizedSPLH(parsedData, 'p');
            Object.assign(parsedData, previousSPLH);
          }

          // Calculate percentage change if we have previous year data
          parsedData.pct = calculatePercentageChange(parsedData.cNSTotal, parsedData.pNSTotal)

          // Update the row with recalculated SPLH
          mergedData.data[index].rowData = formatStoreData(parsedData);
        }
      });

    } else {

      // 1) Build quick lookups for comp & cur Toast rows by store name
      const compMap = new Map(compItem?.data.map(r => [r.rowData.store, r]))
      const curMap  = new Map(curItem?.data.map(r  => [r.rowData.store, r ]))

      // 2) Get the union of all stores that appear in either comp or cur
      const allStores = new Set([
        ...compMap.keys(),
        ...curMap.keys(),
      ])

      // 3) For each store, assemble a merged row

      mergedData.data = Array.from(allStores).map(storeName => {
        // a) pick a base row (for manager & rowType) from comp or cur
        const sourceRow = compMap.get(storeName) || curMap.get(storeName)
        const base      = processStoreRow(sourceRow)

        // b) shallow-copy its rowData
        const row = { ...base.rowData }

        // c) OVERWRITE pXXX from comp, if we have it
        const compRow = compMap.get(storeName)
        if (compRow) {
          Object.keys(compRow.rowData)
            .filter(k => k.startsWith('c'))
            .forEach(k => {
              row['p' + k.slice(1)] = compRow.rowData[k]
            })
        }

        // d) OVERWRITE cXXX from cur, if we have it
        const curRow = curMap.get(storeName)
        if (curRow) {
          Object.keys(curRow.rowData)
            .filter(k => k.startsWith('c'))
            .forEach(k => {
              row[k] = curRow.rowData[k]
            })
        } else {
          // If no current period data, initialize current period fields to zero
          // This ensures closed stores with only comparison data show up properly
          row.cCCTotal = '0'
          row.cCCDineIn = '0'
          row.cCCToGo = '0'
          row.cCCDelivery = '0'
          row.cNSTotal = '$0'
          row.cNSDineIn = '$0'
          row.cNSToGo = '$0'
          row.cNSDelivery = '$0'
          row.cGSDelivery = '$0'
          row.cACTotal = '0.00'
          row.cACDineIn = '0.00'
          row.cACToGo = '0.00'
          row.cACDelivery = '0.00'
          row.cSPLHTotal = '0'
          row.cBOH = '$0'
          row.cFOH = '$0'
          row.cOTTotal = '$0'
          row.cOTBOH = '$0'
          row.cOTFOH = '$0'
          row.cLaborPct = '0.00%'
          row.cLaborDollars = '$0'
          row.cFOHHours = 0
          row.cBOHHours = 0
        }

        // e) Handle hours data properly for both years
        // Current year hours from current Toast data
        if (curRow && curRow.rowData._fohRegularHours !== undefined) {
          row.cFOHHours = curRow.rowData._fohOvertimeHours + curRow.rowData._fohRegularHours
          row.cBOHHours = curRow.rowData._bohOvertimeHours + curRow.rowData._bohRegularHours
        }
        
        // Previous year hours from comp Toast data
        if (compRow && compRow.rowData._fohRegularHours !== undefined) {
          row.pFOHHours = compRow.rowData._fohOvertimeHours + compRow.rowData._fohRegularHours
          row.pBOHHours = compRow.rowData._bohOvertimeHours + compRow.rowData._bohRegularHours
        }

        // e) Apply your parsing, averages, pct calculation, and formatting
        const parsed = {
          ...row,
          cCCTotal: parseNumberString(row.cCCTotal),
          pCCTotal: parseNumberString(row.pCCTotal || '0'),
          cCCDineIn: parseNumberString(row.cCCDineIn),
          pCCDineIn: parseNumberString(row.pCCDineIn || '0'),
          cCCToGo: parseNumberString(row.cCCToGo),
          pCCToGo: parseNumberString(row.pCCToGo || '0'),
          cCCDelivery: parseNumberString(row.cCCDelivery),
          pCCDelivery: parseNumberString(row.pCCDelivery || '0'),
          cNSTotal: convertCurrencyStringToNumber(row.cNSTotal),
          pNSTotal: convertCurrencyStringToNumber(row.pNSTotal || '0'),
          cNSDineIn: convertCurrencyStringToNumber(row.cNSDineIn),
          pNSDineIn: convertCurrencyStringToNumber(row.pNSDineIn || '0'),
          cNSToGo: convertCurrencyStringToNumber(row.cNSToGo),
          pNSToGo: convertCurrencyStringToNumber(row.pNSToGo || '0'),
          cNSDelivery: convertCurrencyStringToNumber(row.cNSDelivery),
          pNSDelivery: convertCurrencyStringToNumber(row.pNSDelivery || '0'),
          cGSDelivery: convertCurrencyStringToNumber(row.cGSDelivery),
          pGSDelivery: convertCurrencyStringToNumber(row.pGSDelivery || '0'),
          cBOH: convertCurrencyStringToNumber(row.cBOH),
          pBOH: convertCurrencyStringToNumber(row.pBOH || '0'),
          cFOH: convertCurrencyStringToNumber(row.cFOH),
          pFOH: convertCurrencyStringToNumber(row.pFOH || '0'),
          cOTTotal: convertCurrencyStringToNumber(row.cOTTotal),
          pOTTotal: convertCurrencyStringToNumber(row.pOTTotal || '0'),
          cOTBOH: convertCurrencyStringToNumber(row.cOTBOH),
          pOTBOH: convertCurrencyStringToNumber(row.pOTBOH || '0'),
          cOTFOH: convertCurrencyStringToNumber(row.cOTFOH),
          pOTFOH: convertCurrencyStringToNumber(row.pOTFOH || '0'),
          cLaborPct: parsePercentString(row.cLaborPct),
          pLaborPct: parsePercentString(row.pLaborPct || '0%'),
          cFOHHours: parseFloat(row.cFOHHours || 0),
          cBOHHours: parseFloat(row.cBOHHours || 0),
          pFOHHours: parseFloat(row.pFOHHours || 0),
          pBOHHours: parseFloat(row.pBOHHours || 0)
        }
        // Calculate averages
        parsed.cACTotal = calculateAverageCheck(parsed.cNSTotal, parsed.cCCTotal);
        parsed.cACDineIn = calculateAverageCheck(parsed.cNSDineIn, parsed.cCCDineIn);
        parsed.cACToGo = calculateAverageCheck(parsed.cNSToGo, parsed.cCCToGo);
        parsed.cACDelivery = calculateAverageCheck(parsed.cNSDelivery, parsed.cCCDelivery);
        parsed.pACTotal = calculateAverageCheck(parsed.pNSTotal, parsed.pCCTotal);
        parsed.pACDineIn = calculateAverageCheck(parsed.pNSDineIn, parsed.pCCDineIn);
        parsed.pACToGo = calculateAverageCheck(parsed.pNSToGo, parsed.pCCToGo);
        parsed.pACDelivery = calculateAverageCheck(parsed.pNSDelivery, parsed.pCCDelivery);

        // STANDARDIZED SPLH CALCULATION - Recalculate for both years
        const currentSPLH = calculateStandardizedSPLH(parsed, 'c');
        Object.assign(parsed, currentSPLH);
        
        // Only calculate previous year SPLH if we have previous year data
        if (parsed.pNSTotal && parsed.pFOHHours !== undefined && parsed.pBOHHours !== undefined) {
          const previousSPLH = calculateStandardizedSPLH(parsed, 'p');
          Object.assign(parsed, previousSPLH);
        }

        parsed.pct = calculatePercentageChange(parsed.cNSTotal, parsed.pNSTotal)

        return {
          rowType: base.rowType,
          storeId: base.storeId,
          thresholds: base.thresholds || {},
          rowData: formatStoreData(parsed)
        }
      })
    }

    result.push(mergedData);
  });

  // Log final merged results for debugging
  result.forEach(({ pageId, data }) => {
    const otherStoreRows = data.filter(row => row.rowType === 'otherStoreRow');
    console.log(`mergePOSData2 - ${pageId} contains ${otherStoreRows.length} otherStoreRows:`, 
      otherStoreRows.map(r => ({ store: r.rowData.store, cNSTotal: r.rowData.cNSTotal, pNSTotal: r.rowData.pNSTotal })));
  });
  
  console.log('--- MERGE POS DATA EXIT ---')
  return result;
}

// Helper function to convert data to number
function convertCurrencyStringToNumber(currencyString) {
  // Check if the input is a string
  if (typeof currencyString === 'string') {
    // Handle empty strings
    if (currencyString.trim() === '') return 0;
    // Remove currency symbol and commas, then convert to a float
    const parsed = parseFloat(currencyString.replace(/[$,]/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  } else if (typeof currencyString === 'number') {
    // If it's already a number, just return it
    return isNaN(currencyString) ? 0 : currencyString;
  } else {
    // If it's neither a string nor a number (e.g., null or undefined), return 0
    return 0;
  }
}

function parseNumberString(numString) {
  if (typeof numString === 'string') {
    const parsed = parseInt(numString.replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }
  if (typeof numString === 'number') {
    return isNaN(numString) ? 0 : numString;
  }
  return 0;
}

const parsePercentString = (percentString) => {
  if (typeof percentString === 'number') {
    return isNaN(percentString) ? 0 : percentString / 100; // Already a number, just convert to decimal
  }
  if (typeof percentString === 'string') {
    // Remove % and convert to float, then divide by 100
    const parsed = parseFloat(percentString.replace('%', ''));
    return isNaN(parsed) ? 0 : parsed / 100;
  }
  return 0; // Handle null/undefined/invalid input
}

// Helper function to calculate average check
const calculateAverageCheck = (netSales, checkCount) => {  
  if (!netSales || !checkCount || checkCount === 0 || isNaN(netSales) || isNaN(checkCount)) {
    return 0;
  }
  return netSales / checkCount;
};

// Helper function to calculate percentage change
const calculatePercentageChange = (current, prior) => {
  if (!prior || prior === 0 || isNaN(current) || isNaN(prior)) return 0;
  return ((current - prior) / prior) * 100;
};


// Helper function to format store data
const formatStoreData = (data) => {
  // Helper to safely format values that might already be strings
  const formatValue = (value, isString = false) => {
    if (value === undefined || value === null) return isString ? '0' : 0;
    if (typeof value === 'string') return value; // Already formatted
    return isString ? value.toString() : value;
  };
  
  return {
    ...data,
    cCCTotal: formatValue(data.cCCTotal, true),
    pCCTotal: formatValue(data.pCCTotal, true),
    cCCDineIn: formatValue(data.cCCDineIn, true),
    pCCDineIn: formatValue(data.pCCDineIn, true),
    cCCToGo: formatValue(data.cCCToGo, true),
    pCCToGo: formatValue(data.pCCToGo, true),
    cCCDelivery: formatValue(data.cCCDelivery, true),
    pCCDelivery: formatValue(data.pCCDelivery, true),
    cNSTotal: typeof data.cNSTotal === 'string' ? data.cNSTotal : `${(data.cNSTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    pNSTotal: typeof data.pNSTotal === 'string' ? data.pNSTotal : `${(data.pNSTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    cNSDineIn: typeof data.cNSDineIn === 'string' ? data.cNSDineIn : `${(data.cNSDineIn || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    pNSDineIn: typeof data.pNSDineIn === 'string' ? data.pNSDineIn : `${(data.pNSDineIn || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    cNSToGo: typeof data.cNSToGo === 'string' ? data.cNSToGo : `${(data.cNSToGo || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    pNSToGo: typeof data.pNSToGo === 'string' ? data.pNSToGo : `${(data.pNSToGo || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    cNSDelivery: typeof data.cNSDelivery === 'string' ? data.cNSDelivery : `${(data.cNSDelivery || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    pNSDelivery: typeof data.pNSDelivery === 'string' ? data.pNSDelivery : `${(data.pNSDelivery || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    cGSDelivery: typeof data.cGSDelivery === 'string' ? data.cGSDelivery : `${(data.cGSDelivery || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    pGSDelivery: typeof data.pGSDelivery === 'string' ? data.pGSDelivery : `${(data.pGSDelivery || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    cACTotal: typeof data.cACTotal === 'string' ? data.cACTotal : `${(data.cACTotal || 0).toFixed(2)}`,
    cACDineIn: typeof data.cACDineIn === 'string' ? data.cACDineIn : `${(data.cACDineIn || 0).toFixed(2)}`,
    cACToGo: typeof data.cACToGo === 'string' ? data.cACToGo : `${(data.cACToGo || 0).toFixed(2)}`,
    cACDelivery: typeof data.cACDelivery === 'string' ? data.cACDelivery : `${(data.cACDelivery || 0).toFixed(2)}`,
    pACTotal: typeof data.pACTotal === 'string' ? data.pACTotal : `${(data.pACTotal || 0).toFixed(2)}`,
    pACDineIn: typeof data.pACDineIn === 'string' ? data.pACDineIn : `${(data.pACDineIn || 0).toFixed(2)}`,
    pACToGo: typeof data.pACToGo === 'string' ? data.pACToGo : `${(data.pACToGo || 0).toFixed(2)}`,
    pACDelivery: typeof data.pACDelivery === 'string' ? data.pACDelivery : `${(data.pACDelivery || 0).toFixed(2)}`,
    // SPLH values - rounded to 0 decimal places
    cSPLHTotal: typeof data.cSPLHTotal === 'string' ? data.cSPLHTotal : `${(data.cSPLHTotal || 0)}`,
    cSPLHFOH: typeof data.cSPLHFOH === 'string' ? data.cSPLHFOH : `${(data.cSPLHFOH || 0)}`,
    cSPLHBOH: typeof data.cSPLHBOH === 'string' ? data.cSPLHBOH : `${(data.cSPLHBOH || 0)}`,
    pSPLHTotal: typeof data.pSPLHTotal === 'string' ? data.pSPLHTotal : `${(data.pSPLHTotal || 0)}`,
    pSPLHFOH: typeof data.pSPLHFOH === 'string' ? data.pSPLHFOH : `${(data.pSPLHFOH || 0)}`,
    pSPLHBOH: typeof data.pSPLHBOH === 'string' ? data.pSPLHBOH : `${(data.pSPLHBOH || 0)}`,
    cFOH: typeof data.cFOH === 'string' ? data.cFOH : `${(data.cFOH || 0).toFixed(0)}`,
    pFOH: typeof data.pFOH === 'string' ? data.pFOH : `${(data.pFOH || 0).toFixed(0)}`,
    cBOH: typeof data.cBOH === 'string' ? data.cBOH : `${(data.cBOH || 0).toFixed(0)}`,
    pBOH: typeof data.pBOH === 'string' ? data.pBOH : `${(data.pBOH || 0).toFixed(0)}`,
    cOTTotal: typeof data.cOTTotal === 'string' ? data.cOTTotal : `${(data.cOTTotal || 0).toFixed(0)}`,
    pOTTotal: typeof data.pOTTotal === 'string' ? data.pOTTotal : `${(data.pOTTotal || 0).toFixed(0)}`,
    cOTFOH: typeof data.cOTFOH === 'string' ? data.cOTFOH : `${(data.cOTFOH || 0).toFixed(0)}`,
    pOTFOH: typeof data.pOTFOH === 'string' ? data.pOTFOH : `${(data.pOTFOH || 0).toFixed(0)}`,
    cOTBOH: typeof data.cOTBOH === 'string' ? data.cOTBOH : `${(data.cOTBOH || 0).toFixed(0)}`,
    pOTBOH: typeof data.pOTBOH === 'string' ? data.pOTBOH : `${(data.pOTBOH || 0).toFixed(0)}`,
    cLaborPct: typeof data.cLaborPct === 'string' ? data.cLaborPct : `${((data.cLaborPct || 0) * 100).toFixed(2)}%`,
    pLaborPct: typeof data.pLaborPct === 'string' ? data.pLaborPct : `${((data.pLaborPct || 0) * 100).toFixed(2)}%`,
    pct: typeof data.pct === 'string' ? data.pct : `${(data.pct || 0).toFixed(2)}%`
  };
};